import CorporateWebBoundary from '@/components/CorporateWebBoundary';
import '@/App.css';
import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { canAccess, homeRoute } from '@/lib/roles';
import { Toaster } from 'sonner';
import PublicationPreviewBanner from '@/components/PublicationPreviewBanner';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import AccountScope from '@/components/AccountScope';
import NativeMobileSession from '@/components/NativeMobileSession';
import MfaGate from '@/components/MfaGate';
import VerifyAccess from '@/pages/VerifyAccess';
import ProfileTestSelector, { ProfileTestBanner } from '@/pages/ProfileTestSelector';
import InsurerTourOverlay from '@/components/tour/InsurerTour';
import Landing from '@/pages/Landing';
import PublicWebLanding from '@/pages/PublicWebLanding';
import PublicServicePage from '@/pages/PublicServicePage';
import { PUBLIC_SERVICE_PAGES, servicePath } from '@/lib/publicServicePages';
import RescueGalleryAdmin from '@/pages/RescueGalleryAdmin';
import SiteMetadata from '@/components/SiteMetadata';
import CookieConsent from '@/components/CookieConsent';
import BusinessStructuredData from '@/components/BusinessStructuredData';

const ChangePassword = lazy(() => import('@/pages/ChangePassword'));
const ActivateAccount = lazy(() => import('@/pages/ActivateAccount'));
const NotificationsCenter = lazy(() => import('@/pages/NotificationsCenter'));
const Login = lazy(() => import('@/pages/Login'));
const CustomerPortal = lazy(() => import('@/pages/CustomerPortal'));
const OnboardingPage = lazy(() => import('@/pages/OnboardingPage'));
const RegistrationAccess = lazy(() => import('@/pages/RegistrationAccess'));
const PhoneVerification = lazy(() => import('@/pages/PhoneVerification'));
const CustomerRegister = lazy(() => import('@/pages/CustomerRegister'));
const CustomerVehicles = lazy(() => import('@/pages/CustomerVehicles'));
const CustomerProfile = lazy(() => import('@/pages/CustomerProfile'));
const CustomerPhoneVerification = lazy(() => import('@/pages/CustomerPhoneVerification'));
const CustomerServices = lazy(() => import('@/pages/CustomerServices'));
const CustomerCheckout = lazy(() => import('@/pages/CustomerCheckout'));
const CustomerRequest = lazy(() => import('@/pages/CustomerRequest'));
const PartnerApp = lazy(() => import('@/pages/PartnerApp'));
const ForgotPassword = lazy(() => import('@/pages/AuthReset').then(module => ({ default: module.ForgotPassword })));
const ResetPassword = lazy(() => import('@/pages/AuthReset').then(module => ({ default: module.ResetPassword })));
const PublicFleet = lazy(() => import('@/pages/PublicFleet'));
const Dispatch = lazy(() => import('@/pages/Dispatch'));
const CreateService = lazy(() => import('@/pages/CreateService'));
const ServiceDetail = lazy(() => import('@/pages/ServiceDetail'));
const Fleet = lazy(() => import('@/pages/Fleet'));
const Drivers = lazy(() => import('@/pages/Drivers'));
const Reports = lazy(() => import('@/pages/Reports'));
const AdminRates = lazy(() => import('@/pages/AdminRates'));
const Invoices = lazy(() => import('@/pages/Invoices'));
const AdminZones = lazy(() => import('@/pages/AdminZones'));
const CompanySettings = lazy(() => import('@/pages/CompanySettings'));
const PhotoGallery = lazy(() => import('@/pages/PhotoGallery'));
const NotificationsAdmin = lazy(() => import('@/pages/NotificationsAdmin'));
const AdminSettings = lazy(() => import('@/pages/AdminSettings'));
const ServiceTypesTab = lazy(() => import('@/pages/settings/ServiceTypesTab'));
const NumberingTab = lazy(() => import('@/pages/settings/NumberingTab'));
const SystemDefaultsTab = lazy(() => import('@/pages/settings/SystemDefaultsTab'));
const TaxSettingsTab = lazy(() => import('@/pages/settings/TaxSettingsTab'));
const NotificationTemplatesTab = lazy(() => import('@/pages/settings/NotificationTemplatesTab'));
const ApiAccessTab = lazy(() => import('@/pages/settings/ApiAccessTab'));
const ReputationTab = lazy(() => import('@/pages/settings/ReputationTab'));
const AdminRatesEditor = lazy(() => import('@/pages/AdminRatesEditor'));
const AdminInsurers = lazy(() => import('@/pages/AdminInsurers'));
const AdminCorporate = lazy(() => import('@/pages/AdminCorporate'));
const AdminPortfolio = lazy(() => import('@/pages/AdminPortfolio'));
const AccountingLayout = lazy(() => import('@/pages/accounting/AccountingLayout'));
const AccountingDashboard = lazy(() => import('@/pages/accounting/AccountingDashboard'));
const ServiceMonthlySummary = lazy(() => import('@/pages/accounting/ServiceMonthlySummary'));
const AccountingPurchases = lazy(() => import('@/pages/accounting/AccountingPurchases'));
const AccountingSuppliers = lazy(() => import('@/pages/accounting/AccountingSuppliers'));
const AccountingPayable = lazy(() => import('@/pages/accounting/AccountingPayable'));
const AccountingReceivable = lazy(() => import('@/pages/accounting/AccountingReceivable'));
const AccountingPeriods = lazy(() => import('@/pages/accounting/AccountingPeriods'));
const AccountingReconciliation = lazy(() => import('@/pages/accounting/AccountingReconciliation'));
const AccountingTaxRules = lazy(() => import('@/pages/accounting/AccountingTaxRules'));
const AccountingCostCenters = lazy(() => import('@/pages/accounting/AccountingCostCenters'));
const LedgerCOA = lazy(() => import('@/pages/accounting/LedgerCOA'));
const LedgerJournal = lazy(() => import('@/pages/accounting/LedgerJournal'));
const LedgerReports = lazy(() => import('@/pages/accounting/LedgerReports'));
const OpeningBalances = lazy(() => import('@/pages/accounting/OpeningBalances'));
const ManualIncome = lazy(() => import('@/pages/accounting/ManualIncome'));
const PaymentsList = lazy(() => import('@/pages/accounting/PaymentsList'));
const CustomerPaymentReview = lazy(() => import('@/pages/accounting/CustomerPaymentReview'));
const BanksCash = lazy(() => import('@/pages/accounting/BanksCash'));
const DriverCashDaily = lazy(() => import('@/pages/accounting/DriverCashDaily'));
const FiscalBackfillPreview = lazy(() => import('@/pages/accounting/FiscalBackfillPreview'));
const DGIIAssistant = lazy(() => import('@/pages/accounting/DGIIAssistant'));
const Payroll = lazy(() => import('@/pages/accounting/Payroll'));
const Profitability = lazy(() => import('@/pages/accounting/Profitability'));
const BankImport = lazy(() => import('@/pages/accounting/BankImport'));
const AdminBillingReports = lazy(() => import('@/pages/AdminBillingReports'));
const AdminDGII = lazy(() => import('@/pages/AdminDGII'));
const AdminPurchases = lazy(() => import('@/pages/AdminPurchases'));
const AdminNCFSequences = lazy(() => import('@/pages/AdminNCFSequences'));
const AdminSettlements = lazy(() => import('@/pages/AdminSettlements'));
const GoogleReviewsTab = lazy(() => import('@/pages/settings/GoogleReviewsTab'));
const Achievements = lazy(() => import('@/pages/Achievements'));
const PartnerWeightsTab = lazy(() => import('@/pages/settings/PartnerWeightsTab'));
const PartnerPresetsTab = lazy(() => import('@/pages/settings/PartnerPresetsTab'));
const DriverWeightsTab = lazy(() => import('@/pages/settings/DriverWeightsTab'));
const AutoDispatchTab = lazy(() => import('@/pages/settings/AutoDispatchTab'));
const UsersSecurity = lazy(() => import('@/pages/UsersSecurity'));
const DispatchContacts = lazy(() => import('@/pages/DispatchContacts'));
const ProviderSettlements = lazy(() => import('@/pages/ProviderSettlements'));
const InsuranceTeam = lazy(() => import('@/pages/InsuranceTeam'));
const InsuranceMembershipAdmin = lazy(() => import('@/pages/InsuranceMembershipAdmin'));
const OperationalCases = lazy(() => import('@/pages/OperationalCases'));
const DispatchConsole = lazy(() => import('@/pages/DispatchConsole'));
const AccountDeletion = lazy(() => import('@/pages/AccountDeletion'));
const AdminPartners = lazy(() => import('@/pages/AdminPartners'));
const AdminDrivers = lazy(() => import('@/pages/AdminDrivers'));
const DriverLeaderboard = lazy(() => import('@/pages/DriverLeaderboard'));
const FleetMaintenance = lazy(() => import('@/pages/FleetMaintenance'));
const ExecutiveDashboard = lazy(() => import('@/pages/admin/ExecutiveDashboard'));
const ServiceAlertsSettings = lazy(() => import('@/pages/admin/ServiceAlertsSettings'));
const Privacy = lazy(() => import('@/pages/Privacy'));
const Terms = lazy(() => import('@/pages/Terms'));
const Contact = lazy(() => import('@/pages/Contact'));
const PushDevices = lazy(() => import('@/pages/admin/PushDevices'));
const DriverApp = lazy(() => import('@/pages/DriverApp'));
const InsurancePortal = lazy(() => import('@/pages/InsurancePortal'));
const BusinessPortal = lazy(() => import('@/pages/BusinessPortal'));
const PublicRequest = lazy(() => import('@/pages/PublicRequest'));
const PublicTracking = lazy(() => import('@/pages/PublicTracking'));
const ApiDocsPortal = lazy(() => import('@/pages/ApiDocsPortal'));
const AdminEmployees = lazy(() => import('@/pages/AdminEmployees'));
const DataCleanup = lazy(() => import('@/pages/DataCleanup'));
const WhatsAppBotSimulator = lazy(() => import('@/pages/WhatsAppBotSimulator'));
const WhatsAppConversations = lazy(() => import('@/pages/WhatsAppConversations'));
const WhatsAppOpsHealth = lazy(() => import('@/pages/WhatsAppOpsHealth'));
const NotFound = lazy(() => import('@/pages/NotFound'));

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-neutral-500">Cargando…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password && pathname !== '/change-password') return <Navigate to="/change-password" replace />;
  if (!canAccess(user, roles, pathname)) {
    return <Navigate to="/app" replace />;
  }
  return <AccountScope user={user}>{children}</AccountScope>;
}

function NativeAwareHome() {
  const { user, loading } = useAuth();

  if (!Capacitor.isNativePlatform()) return <PublicWebLanding />;
  if (loading) {
    return <div className="min-h-[100dvh] flex items-center justify-center bg-black text-neutral-400">Cargando C&A…</div>;
  }

  return <AccountScope user={user}><CustomerPortal authenticated={user?.role === 'customer'} /></AccountScope>;
}

function RoleRouter() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-neutral-500">Cargando…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  return <Navigate to={homeRoute(user)} replace />;
}

function ProfileTestBottomSpace() {
  const { user } = useAuth();
  return user?.profile_test ? <div aria-hidden="true" className="h-32" /> : null;
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter><CorporateWebBoundary><MfaGate><ProfileTestBanner />
          <PublicationPreviewBanner />
          <SiteMetadata />
          <BusinessStructuredData />
          <CookieConsent />
          <NativeMobileSession />
          <InsurerTourOverlay />
          <Toaster theme="dark" richColors position="top-right" />
          <Suspense fallback={<main className="min-h-screen bg-black p-8 text-neutral-300" role="status">Cargando C&A…</main>}>
          <Routes>
            <Route path="/profiles" element={<Protected roles={['super_admin']}><ProfileTestSelector /></Protected>} />
            <Route path="/verify-access" element={<VerifyAccess />} />
            <Route path="/" element={<NativeAwareHome />} />
            <Route path="/website" element={<PublicWebLanding />} />
            <Route path="/admin/fotos-rescates" element={<Protected roles={['super_admin']}><RescueGalleryAdmin /></Protected>} />
            <Route path="/mobile" element={<CustomerPortal />} />
            <Route path="/customer" element={<Protected roles={['customer']}><CustomerPortal authenticated /></Protected>} />
            <Route path="/customer/register" element={<CustomerRegister />} />
            <Route path="/customer/vehicles" element={<Protected roles={['customer']}><CustomerVehicles /></Protected>} />
            <Route path="/customer/profile" element={<Protected roles={['customer']}><CustomerProfile /></Protected>} />
            <Route path="/customer/verify-phone" element={<Protected roles={['customer']}><CustomerPhoneVerification /></Protected>} />
            <Route path="/customer/services" element={<Protected roles={['customer']}><CustomerServices /></Protected>} />
            <Route path="/customer/request" element={<Protected roles={['customer']}><CustomerRequest /></Protected>} />
            <Route path="/customer/services/:id/checkout" element={<Protected roles={['customer']}><CustomerCheckout /></Protected>} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/activate/:token" element={<ActivateAccount />} />
            {PUBLIC_SERVICE_PAGES.map(page => <Route key={page.slug} path={servicePath(page)} element={<PublicServicePage page={page} />} />)}
            <Route path="/flota" element={<PublicFleet />} />
            <Route path="/solicitar" element={<PublicRequest />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/privacy-policy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/terms-and-conditions" element={<Terms />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/track/:token" element={<PublicTracking />} />
            <Route path="/docs/aseguradoras" element={<ApiDocsPortal />} />
            <Route path="/app" element={<RoleRouter />} />
            <Route path="/registro" element={<OnboardingPage />} />
            <Route path="/registro/iniciar" element={<RegistrationAccess />} />
            <Route path="/registro/telefono" element={<PhoneVerification />} />
            <Route path="/cuenta/eliminar" element={<AccountDeletion />} />
            <Route path="/change-password" element={<Protected><ChangePassword /></Protected>} />
            <Route path="/admin/notifications" element={<Protected roles={['admin','super_admin','dispatcher','accountant','customer_service']}><NotificationsCenter /></Protected>} />
            <Route path="/admin" element={<Protected roles={['admin','super_admin']}><Navigate to="/admin/dashboard-ejecutivo" replace /></Protected>} />
            <Route path="/accounting" element={<Protected roles={['admin','super_admin','accountant']}><Navigate to="/admin/accounting" replace /></Protected>} />
            <Route path="/dispatch" element={<Protected roles={['admin','dispatcher','customer_service']}><Dispatch /></Protected>} />
            <Route path="/dispatch/nuevo" element={<Protected roles={['admin','dispatcher','customer_service','insurance','business']}><CreateService /></Protected>} />
            <Route path="/service/:id" element={<Protected><ServiceDetail /></Protected>} />
            <Route path="/fleet" element={<Protected roles={['admin','dispatcher']}><Fleet /></Protected>} />
            <Route path="/drivers" element={<Protected roles={['admin','dispatcher','super_admin']}><AdminDrivers /></Protected>} />
            <Route path="/reports" element={<Protected roles={['admin','dispatcher','accountant']}><Reports /></Protected>} />
            <Route path="/admin/rates" element={<Protected roles={['admin','super_admin']}><AdminRates /></Protected>} />
            <Route path="/admin/zones" element={<Protected roles={['admin','super_admin']}><AdminZones /></Protected>} />
            <Route path="/admin/company" element={<Protected roles={['admin','super_admin']}><CompanySettings /></Protected>} />
            <Route path="/admin/notifications" element={<Protected roles={['admin','dispatcher']}><NotificationsAdmin /></Protected>} />
            {/* /admin/settings/* — sub-rutas de la sección Configuración vinculadas a componentes existentes */}
            <Route path="/admin/settings/company" element={<Protected roles={['admin','super_admin']}><CompanySettings /></Protected>} />
            <Route path="/admin/settings/branding" element={<Protected roles={['admin','super_admin']}><CompanySettings /></Protected>} />
            <Route path="/admin/settings/zones" element={<Protected roles={['admin','super_admin']}><AdminZones /></Protected>} />
            <Route path="/admin/settings/rates" element={<Protected roles={['admin','super_admin']}><AdminRates /></Protected>} />
            <Route path="/admin/settings/insurers" element={<Protected roles={['admin','super_admin']}><AdminInsurers /></Protected>} />
            <Route path="/admin/settings/corporate" element={<Protected roles={['admin','super_admin']}><AdminCorporate /></Protected>} />
            <Route path="/admin/settings/notifications" element={<Protected roles={['admin','dispatcher','super_admin']}><NotificationsAdmin /></Protected>} />
            <Route path="/admin/settings/service-alerts" element={<Protected roles={['admin','super_admin']}><ServiceAlertsSettings /></Protected>} />
            <Route path="/admin/settings/push-devices" element={<Protected roles={['admin','super_admin','dispatcher','customer_service']}><PushDevices /></Protected>} />
            <Route path="/admin/finance/portfolio" element={<Protected roles={['admin','super_admin']}><AdminPortfolio /></Protected>} />
            <Route path="/admin/accounting" element={<Protected roles={['admin','super_admin','accountant']}><AccountingLayout /></Protected>}>
              <Route index element={<AccountingDashboard />} />
              <Route path="service-summary" element={<ServiceMonthlySummary />} />
              <Route path="purchases" element={<AccountingPurchases />} />
              <Route path="suppliers" element={<AccountingSuppliers />} />
              <Route path="payable" element={<AccountingPayable />} />
              <Route path="receivable" element={<AccountingReceivable />} />
              <Route path="periods" element={<AccountingPeriods />} />
              <Route path="reconciliation" element={<AccountingReconciliation />} />
              <Route path="tax-rules" element={<AccountingTaxRules />} />
              <Route path="cost-centers" element={<AccountingCostCenters />} />
              <Route path="coa" element={<LedgerCOA />} />
              <Route path="journal" element={<LedgerJournal />} />
              <Route path="reports" element={<LedgerReports />} />
              <Route path="opening-balances" element={<OpeningBalances />} />
              <Route path="manual-income" element={<ManualIncome />} />
              <Route path="payments" element={<PaymentsList />} />
              <Route path="customer-payments" element={<CustomerPaymentReview />} />
              <Route path="banks-cash" element={<BanksCash />} />
              <Route path="driver-cash" element={<DriverCashDaily />} />
              <Route path="bank-import" element={<BankImport />} />
              <Route path="fiscal-backfill" element={<FiscalBackfillPreview />} />
              <Route path="dgii" element={<DGIIAssistant />} />
              <Route path="payroll" element={<Payroll />} />
              <Route path="profitability" element={<Profitability />} />
            </Route>
            <Route path="/admin/security" element={<Protected roles={['admin','super_admin']}><UsersSecurity /></Protected>} />
            <Route path="/admin/seguimiento-contactos" element={<Protected roles={['super_admin']}><DispatchContacts /></Protected>} />
            <Route path="/admin/provider-liquidations" element={<Protected roles={['super_admin','accountant']}><ProviderSettlements /></Protected>} />
            <Route path="/admin/insurance-access" element={<Protected roles={['super_admin']}><InsuranceMembershipAdmin /></Protected>} />
            <Route path="/insurance/team" element={<Protected roles={['insurance']}><InsuranceTeam /></Protected>} />
            <Route path="/dispatch/incidents" element={<Protected roles={['super_admin','admin','dispatcher']}><OperationalCases /></Protected>} />
            <Route path="/dispatch/coordination" element={<Protected roles={['super_admin','admin','dispatcher']}><DispatchConsole /></Protected>} />
            <Route path="/admin/partners" element={<Protected roles={['admin','dispatcher']}><AdminPartners /></Protected>} />
            <Route path="/admin/settlements" element={<Protected roles={['admin','super_admin']}><AdminSettlements /></Protected>} />
            <Route path="/admin/rates-editor" element={<Protected roles={['admin','super_admin']}><AdminRatesEditor /></Protected>} />
            <Route path="/admin/reports/billing" element={<Protected roles={['admin','super_admin']}><AdminBillingReports /></Protected>} />
            <Route path="/admin/billing/dgii" element={<Protected roles={['admin','super_admin','accountant']}><AdminDGII /></Protected>} />
            <Route path="/admin/finance/purchases" element={<Protected roles={['admin','super_admin','accountant']}><AdminPurchases /></Protected>} />
            <Route path="/admin/billing/ncf-sequences" element={<Protected roles={['super_admin']}><AdminNCFSequences /></Protected>} />
            <Route path="/admin/achievements" element={<Protected roles={['admin','dispatcher','super_admin']}><Achievements /></Protected>} />
            <Route path="/driver/achievements" element={<Protected roles={['driver']}><Achievements isDriverView={true} /></Protected>} />
            <Route path="/admin/drivers" element={<Protected roles={['admin','dispatcher','super_admin']}><AdminDrivers /></Protected>} />
            <Route path="/admin/employees" element={<Protected roles={['admin','super_admin','dispatcher','accountant']}><AdminEmployees /></Protected>} />
            <Route path="/admin/data-cleanup" element={<Protected roles={['super_admin']}><DataCleanup /></Protected>} />
            <Route path="/admin/whatsapp-bot-simulator" element={<Protected roles={['admin','super_admin']}><WhatsAppBotSimulator /></Protected>} />
            <Route path="/admin/whatsapp-conversations" element={<Protected roles={['admin','super_admin']}><WhatsAppConversations /></Protected>} />
            <Route path="/admin/whatsapp-ops-health" element={<Protected roles={['admin','super_admin']}><WhatsAppOpsHealth /></Protected>} />
            <Route path="/leaderboard" element={<Protected roles={['admin','dispatcher','super_admin']}><DriverLeaderboard /></Protected>} />
            <Route path="/driver/leaderboard" element={<Protected roles={['driver']}><DriverLeaderboard isDriverView={true} /></Protected>} />
            <Route path="/fleet/maintenance" element={<Protected roles={['admin','dispatcher','super_admin']}><FleetMaintenance /></Protected>} />
            <Route path="/admin/dashboard-ejecutivo" element={<Protected roles={['admin','super_admin','dispatcher']}><ExecutiveDashboard /></Protected>} />
            <Route path="/admin/settings" element={<Protected roles={['admin','super_admin']}><AdminSettings /></Protected>}>
              <Route path="service-types" element={<ServiceTypesTab />} />
              <Route path="tax" element={<TaxSettingsTab />} />
              <Route path="numbering" element={<NumberingTab />} />
              <Route path="system" element={<SystemDefaultsTab />} />
              <Route path="templates" element={<NotificationTemplatesTab />} />
              <Route path="api-access" element={<ApiAccessTab />} />
              <Route path="partner-weights" element={<PartnerWeightsTab />} />
              <Route path="google-reviews" element={<GoogleReviewsTab />} />
              <Route path="reputation" element={<ReputationTab />} />
              <Route path="partner-presets" element={<PartnerPresetsTab />} />
              <Route path="driver-weights" element={<DriverWeightsTab />} />
              <Route path="auto-dispatch" element={<AutoDispatchTab />} />
            </Route>
            <Route path="/gallery" element={<Protected roles={['admin','dispatcher','insurance','business']}><PhotoGallery /></Protected>} />
            <Route path="/invoices" element={<Protected roles={['admin','dispatcher','insurance','business','accountant']}><Invoices /></Protected>} />
            <Route path="/driver" element={<Protected roles={['driver']}><DriverApp /></Protected>} />
            <Route path="/partner" element={<Protected roles={['partner']}><PartnerApp /></Protected>} />
            <Route path="/insurance" element={<Protected roles={['insurance']}><InsurancePortal /></Protected>} />
            <Route path="/business" element={<Protected roles={['business']}><BusinessPortal /></Protected>} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        <ProfileTestBottomSpace /></MfaGate></CorporateWebBoundary></BrowserRouter>
      </AuthProvider>
    </div>
  );
}
